import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Subscription, catchError, of, tap } from 'rxjs';
import { TranslationUtils } from 'src/app/app-translation-utils';
import { TaskStatus } from 'src/app/model/TaskStatus';
import { EventService } from 'src/app/service/event.service';
import { SoftwareInventoryService } from 'src/app/service/software-inventory.service';
import { Events, MsgPayLoad, WebsocketService } from 'src/app/service/websocket.service';
import { Utils } from 'src/app/utils';
import { emptyValidator } from 'src/app/validator/empty-form-validator';
import { ModalConfig } from '../../controls/modal/modal-config';
import { ModalRef } from '../../controls/modal/modal-ref';
import { ProductAuthenticationRequest } from 'src/app/model/Product';
import { BaseComponent } from 'src/app/model/BaseComponent';
import { Modal } from '../../controls/modal/modal.service';
import { AuthenticationService } from 'src/app/service/authentication.service';
import { SolutionService } from 'src/app/service/solution.service';

export class ProductLoginResult {
  ok: boolean = false;
  status: string;
  url: string;
  token: string;

  constructor(ok: boolean, url: string, token: string) {
    this.ok = ok;
    this.url = url;
    this.token = token;
  }
}

@Component({
    selector: 'app-login-package-logs',
    templateUrl: './login-package-logs.component.html',
    styleUrls: ['./login-package-logs.component.css'],
    standalone: false
})
export class LoginPackageLogsComponent extends BaseComponent implements OnInit {

  @ViewChild('url') urlInputRef: ElementRef;
  @ViewChild('user') userInputRef: ElementRef;

  schemes: any[] = [{ scheme: 'https://', selected: true },
  { scheme: 'http://', selected: false }];

  error: boolean = false;
  errorMsg: string;
  inProcess: boolean = false;

  PackageLoginForm = new FormGroup({
    url: new FormControl('', [
      Validators.required
    ]),
    user: new FormControl('', [
      Validators.required, emptyValidator()
    ]),
    password: new FormControl('', [
      Validators.required
    ])
  });

  title: string;
  height: string;
  minheight: string;
  description: string;
  showUpdateURL: boolean = true;

  loginResult: ProductLoginResult;

  eventMessageSubscription: Subscription;

  constructor(private modalConfig: ModalConfig, private modalRef: ModalRef,
    private wsService: WebsocketService, protected eventService: EventService,
    private siService: SoftwareInventoryService, protected modal: Modal, 
    protected authService: AuthenticationService, protected solutionService: SolutionService) {
    super(eventService, modal, authService, solutionService);
    this.title = $localize`Authenticate with ${this.modalConfig.data.product}`;
    this.description = this.modalConfig.data.description;
  }

  get urlCtrl() { return this.PackageLoginForm.get('url'); }
  get userCtrl() { return this.PackageLoginForm.get('user'); }
  get passwordCtrl() { return this.PackageLoginForm.get('password'); }

  ngOnInit(): void {
    this.urlCtrl.setValue(this.modalConfig.data.url);
    
    this.eventMessageSubscription = this.eventService.eventMessage$.subscribe(msg => {
      if (msg.eventType == Events.ValidateHost) {
        if(this.isPendingTransaction(msg.headers.transactionId, true)){
          this.processValidateHost(msg.body);
        }
      } else if (msg.eventType == Events.ProductAuthenticate) {
        if(this.isPendingTransaction(msg.headers.transactionId, true)){
          this.processAuthResponse(msg.body);
        }
      } else if (msg.eventType == Events.SessionExpired) {
        this.close();
      } 
    });
  }

  ngAfterViewInit(): void {
    this.urlInputRef.nativeElement.focus();
  }

  ngOnDestroy(): void {
    this.eventMessageSubscription.unsubscribe();
  }

  authenticate(): void {
    this.setInProcess(true);
    this.login();
  }

  login(): void {
    this.siService.sendProductAuthenticate(this.wsService.getClientId(), this.wsService.getWSEnvId(), this.addTransaction(), new ProductAuthenticationRequest(
      this.modalConfig.data.plugin, this.modalConfig.data.macAddress, this.urlCtrl.value,
      this.userCtrl.value, this.passwordCtrl.value
    )).pipe(
      tap(resp => {
      }),
      catchError(resp => {
        this.errorMsg = TranslationUtils.getTaskErrorMessage("LOGIN_REQUEST_FAILURE", undefined);
        this.error = true;
        this.setInProcess(false);
        return of(resp);
      })).subscribe();
  }

  close(): void {
    this.modalRef.close(undefined);
  }

  setInProcess(active: boolean): void {
    this.inProcess = active;
    if (this.inProcess) {
      this.userCtrl.disable();
      this.passwordCtrl.disable();
      this.urlCtrl.disable();
    } else {
      this.userCtrl.enable();
      this.passwordCtrl.enable();
    }
  }

  processValidateHost(resp: any): void {
    let status: TaskStatus = JSON.parse(resp.msg);
    if (status.completed && status.success) {
      this.login();
    } else {
      this.errorMsg = TranslationUtils.getHostValidationErrorMessage(status.errorCode);
      this.error = true;
      this.setInProcess(false);
    }
  }

  processAuthResponse(resp: any): void {
    let authResponse: TaskStatus = JSON.parse(resp.msg);
    if (authResponse.completed && authResponse.success) {
      this.loginResult = new ProductLoginResult(true, this.urlCtrl.value, authResponse.data);
      this.modalRef.close(this.loginResult);
    } else {
      this.error = true;
      if (authResponse.errorCode == '500') {
        this.errorMsg = $localize`Authentication failed. The server is down or the specified URL is incorrect.`;
      } else {
        this.errorMsg = $localize`Authentication failed. Please check ${this.modalConfig.data.product} logs for more details.`;
      }
      this.setInProcess(false);
    }
  }

  updateProductURL(): void {
    this.siService.updateProductURL(this.modalConfig.data.macAddress, this.modalConfig.data.installDirectory,
      this.urlCtrl.value).pipe(
        tap((resp: any) => {
          let status: TaskStatus = resp.body;
          if(status.success){
            let msg = new MsgPayLoad(undefined, {
              message: $localize`The ${this.modalConfig.data.product} URL has been succesfully updated.`,
            });
            msg.setEvent(Events.SuccessMsg);
            Utils.addHeaderMessageBanner(this.eventService, msg);
            this.modalRef.close(this.loginResult);
          } else {
            this.error = true;
            switch(status.errorCode){
              case 'UPDATE_URL_UNKNOWN_DB_ERROR':
              case 'UPDATE_URL_DB_ERROR':
                this.errorMsg = $localize`The ${this.modalConfig.data.product} URL was not updated, please see the logs for more details.`;
                break;
              case 'UPDATE_URL_PRODUCT_NOT_FOUND':
                this.errorMsg = $localize`The ${this.modalConfig.data.product} URL was not updated, the product could not be found.`;
                break;
            }
            this.setInProcess(false);
          }
        })
      ).subscribe();
  }
}
